import React, { useState } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@epoch-ui/components/Sheet/sheet';
import { Button } from '@epoch-ui/components/Button';
import { cn } from '@epoch-ui/lib/utils';
import { Icon } from '@epoch-ui/components/Icon';
import { COMMUNICATION_REMINDER_MEDIUM } from '@event-editor/tabs/EditEventCommunications/types/communication.types';
import { SlackReminderForm } from '@event-editor/tabs/EditEventCommunications/components/EditEventCommunicationSideSheet/ReminderForms/SlackReminderForm';
import { EmailReminderForm } from '@event-editor/tabs/EditEventCommunications/components/EditEventCommunicationSideSheet/ReminderForms/EmailReminderForm';
import DiscardConfirmModal from '@epoch-components/DiscardConfirmModal/DiscardConfirmModal';

const reminderMediumTypes = [
  {
    medium: COMMUNICATION_REMINDER_MEDIUM.SLACK,
    label: 'Slack',
    icon: <Icon name="slack" />
  },
  {
    medium: COMMUNICATION_REMINDER_MEDIUM.EMAIL,
    label: 'Email',
    icon: <Icon name="mail" />
  }
];

interface EditEventReminderUpsertSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditEventReminderUpsertSheet: React.FC<EditEventReminderUpsertSheetProps> = ({
  isOpen,
  onClose
}) => {
  const [selectedMedium, setSelectedMedium] = useState<COMMUNICATION_REMINDER_MEDIUM | null>(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  const handleMediumSelect = (medium: COMMUNICATION_REMINDER_MEDIUM) => {
    setSelectedMedium(medium);
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      // If form is dirty, show discard modal
      if (isFormDirty && selectedMedium) {
        setShowDiscardModal(true);
      } else {
        // Otherwise, close normally
        setSelectedMedium(null);
        setIsFormDirty(false);
        onClose();
      }
    }
  };

  const handleFormClose = () => {
    setSelectedMedium(null);
    setIsFormDirty(false);
    onClose();
  };

  const handleBack = () => {
    // If form is dirty, show discard modal
    if (isFormDirty) {
      setShowDiscardModal(true);
    } else {
      // Otherwise, go back normally
      setSelectedMedium(null);
      setIsFormDirty(false);
    }
  };

  const handleDiscard = () => {
    setShowDiscardModal(false);
    setSelectedMedium(null);
    setIsFormDirty(false);
    onClose();
  };

  const handleCancelDiscard = () => {
    setShowDiscardModal(false);
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent
          className="tw-m-2 tw-flex tw-h-[calc(100vh-16px)] tw-w-[480px] tw-flex-col tw-gap-0 tw-rounded-3xl tw-p-0 sm:tw-max-w-[480px]"
          aria-describedby="create-reminder-sheet"
        >
          {/* Choose reminder medium */}
          {selectedMedium === null && (
            <>
              <SheetHeader className="tw-align-start tw-mb-0 tw-flex-shrink-0 tw-p-6 tw-pb-0 ">
                <SheetTitle className="tw-mb-0">Select reminder type</SheetTitle>
                {/* For accessibility */}
                <SheetDescription id="create-reminder-sheet" className="tw-sr-only tw-mb-0">
                  Select reminder type
                </SheetDescription>
              </SheetHeader>
              <div className="tw-mt-4 tw-flex tw-flex-grow tw-flex-col tw-gap-3 ">
                {selectedMedium === null && (
                  <div className="tw-flex tw-gap-3 tw-px-6">
                    {reminderMediumTypes.map(({ medium, label, icon }) => (
                      <Button
                        key={medium}
                        variant="outline"
                        className={cn('tw-h-30 tw-inline-block tw-w-full tw-flex-col tw-p-4', {
                          'tw-border-primary-blue': selectedMedium === medium,
                          'tw-border-2': selectedMedium === medium
                        })}
                        onClick={() => handleMediumSelect(medium)}
                      >
                        <div
                          className={cn(
                            'tw-flex tw-h-20 tw-w-full tw-items-center tw-justify-center tw-rounded-[8px] tw-bg-light-grey tw-text-dark-black tw-ring-offset-0',
                            {
                              'tw-bg-light-blue-badge': selectedMedium === medium,
                              'tw-text-primary-blue': selectedMedium === medium
                            }
                          )}
                        >
                          {icon}
                        </div>
                        <div className="tw-mt-3 tw-flex tw-justify-start">
                          <p className="tw-text-sm tw-font-medium tw-leading-4">{label} reminder</p>
                        </div>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Show reminder form */}
          {selectedMedium === COMMUNICATION_REMINDER_MEDIUM.SLACK && (
            <SlackReminderForm
              onSuccess={handleFormClose}
              onDirtyChange={setIsFormDirty}
              onBack={handleBack}
            />
          )}
          {selectedMedium === COMMUNICATION_REMINDER_MEDIUM.EMAIL && (
            <EmailReminderForm
              onSuccess={handleFormClose}
              onDirtyChange={setIsFormDirty}
              onBack={handleBack}
            />
          )}
        </SheetContent>
      </Sheet>

      <DiscardConfirmModal
        open={showDiscardModal}
        onOpenChange={setShowDiscardModal}
        onDiscard={handleDiscard}
        onCancel={handleCancelDiscard}
      />
    </>
  );
};
